import React, { useState, useEffect } from "react"
import { useSnapshotSummary } from "~/api/snapshot_summaries"
import type { Perspective } from "~/api/snapshot_summaries"
import GroupingTabs from "./GroupingTabs"
import type { Grouping } from "./utils"
import PerformanceTable from "./PerformanceTable"
import PortfolioCompanyTable from "./PortfolioCompanyTable"
import PortfolioSummary from "./PortfolioSummary"

type TopTab = "summary" | "explorer"
const VALID_TOP_TABS: TopTab[] = ["summary", "explorer"]

function readTopTabParam(): TopTab {
  const v = new URLSearchParams(window.location.search).get("tab")
  return VALID_TOP_TABS.includes(v as TopTab) ? (v as TopTab) : "summary"
}

const VALID_GROUPINGS: Grouping[] = [
  "series",
  "by_vintage",
  "by_asset_class",
  "by_stage",
  "by_sector",
  "by_portfolio_company",
]
const VALID_PERSPECTIVES: Perspective[] = ["net", "gross"]

function readGroupingParam(): Grouping {
  const v = new URLSearchParams(window.location.search).get("grouping")
  return VALID_GROUPINGS.includes(v as Grouping) ? (v as Grouping) : "series"
}

function readPerspectiveParam(contextScoped: boolean): Perspective {
  if (contextScoped) return "net"
  const v = new URLSearchParams(window.location.search).get("perspective")
  return VALID_PERSPECTIVES.includes(v as Perspective)
    ? (v as Perspective)
    : "net"
}

interface SnapshotSummaryProps {
  year: number
  quarter: number
  contextScoped?: boolean
  quarters?: { label: string; path: string }[]
  contextName?: string
}

const SnapshotSummary = ({
  year,
  quarter,
  contextScoped = false,
  quarters,
  contextName,
}: SnapshotSummaryProps) => {
  const [activeTab, setActiveTab] = useState<TopTab>(readTopTabParam)
  const [grouping, setGrouping] = useState<Grouping>(readGroupingParam)
  const [perspective, setPerspective] = useState<Perspective>(() =>
    readPerspectiveParam(contextScoped),
  )
  const [vintageFrom, setVintageFrom] = useState<number | null>(null)
  const [vintageTo, setVintageTo] = useState<number | null>(null)
  const [filterAssetClasses, setFilterAssetClasses] = useState<string[]>([])
  const [filterStages, setFilterStages] = useState<string[]>([])
  const [filterSectors, setFilterSectors] = useState<string[]>([])
  const [filterPortcoSectors, setFilterPortcoSectors] = useState<string[]>([])
  const [filterSeriesText, setFilterSeriesText] = useState("")
  const [filterPortcoText, setFilterPortcoText] = useState("")
  const {
    data: rawData,
    isLoading,
    error,
  } = useSnapshotSummary(year, quarter, perspective)
  const isBuilding = rawData?.building === true
  const data = isBuilding ? undefined : rawData

  // Sync tab + grouping + view + perspective into the URL for deep-linking and reload.
  // When context-scoped, perspective is always net and is omitted.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    params.set("tab", activeTab)
    params.set("grouping", grouping)
    if (contextScoped) {
      params.delete("perspective")
    } else {
      params.set("perspective", perspective)
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState(window.history.state, "", newUrl)
  }, [activeTab, grouping, perspective, contextScoped])

  useEffect(() => {
    if (grouping !== "series") {
      setVintageFrom(null)
      setVintageTo(null)
      setFilterAssetClasses([])
      setFilterStages([])
      setFilterSectors([])
      setFilterSeriesText("")
    }
    if (grouping !== "by_portfolio_company") {
      setFilterPortcoSectors([])
      setFilterPortcoText("")
    }
  }, [grouping])

  function toggleFilter(
    current: string[],
    value: string,
    setter: (v: string[]) => void,
  ) {
    setter(
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    )
  }

  const vintageOptions = data
    ? Array.from(new Set(data.series.map((r) => r.vintage))).sort()
    : []
  const assetClassOptions = data
    ? (Array.from(
        new Set(data.series.map((r) => r.asset_class_label).filter(Boolean)),
      ) as string[])
    : []
  const stageOptions = data
    ? Array.from(
        new Set(
          data.series.map((r) => r.stage).filter((s): s is string => s != null),
        ),
      )
    : []
  const sectorOptions = data
    ? Array.from(
        new Set(
          data.series
            .map((r) => r.sector)
            .filter((s): s is string => s != null),
        ),
      )
    : []

  const portcoSectorOptions = data
    ? Array.from(
        new Set(
          data.series.flatMap((r) =>
            (r.assets ?? [])
              .filter((a) => !a.is_other && a.sector != null)
              .map((a) => a.sector as string),
          ),
        ),
      )
    : []

  const currentQuarterPath = `/manage/status/snapshot_summaries/${year}/${quarter}`

  return (
    <div className="b-snapshot-summary-container">
      <div className="b-ps-page-header">
        <div className="b-ps-page-header__left">
          <h1 className="b-ps-page-header__title">Portfolio Summary</h1>
          {contextScoped && contextName && (
            <div className="b-ps-page-header__entity">{contextName}</div>
          )}
        </div>
        <div className="b-ps-page-header__right">
          {!contextScoped && (
            <div className="b-snapshot-summary-perspective-toggle">
              <button
                className={`b-snapshot-summary-perspective-btn${
                  perspective === "net" ? " active" : ""
                }`}
                onClick={() => setPerspective("net")}
              >
                Net
              </button>
              <button
                className={`b-snapshot-summary-perspective-btn${
                  perspective === "gross" ? " active" : ""
                }`}
                onClick={() => setPerspective("gross")}
              >
                Gross
              </button>
            </div>
          )}
          {/* Export button removed while the page is in an unstable state.
              Re-add when stable, and update the export logic to match the current UI. */}
          {quarters && quarters.length > 0 && (
            <div className="b-ps-quarter-selector">
              <span className="b-ps-quarter-selector__label">As of</span>
              <select
                className="b-ps-quarter-select"
                value={currentQuarterPath}
                onChange={(e) => {
                  window.location.href = e.target.value
                }}
              >
                {quarters.map((q) => (
                  <option key={q.path} value={q.path}>
                    {q.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <a
            href={`/manage/status/portfolio_summary/${year}/${quarter}`}
            className="b-snapshot-summary-export-btn"
          >
            Portfolio Summary prototype
          </a>
        </div>
      </div>

      {isLoading && (
        <div className="b-snapshot-summary-loading">Loading summary data…</div>
      )}

      {isBuilding && (
        <div className="b-snapshot-summary-loading">
          Building summary… this may take a moment.
        </div>
      )}

      {error && (
        <div className="b-snapshot-summary-error">
          Failed to load summary: {error.message}
        </div>
      )}

      {data && (
        <>
          <div className="b-ps-top-tabs">
            <button
              className={`b-ps-top-tab${
                activeTab === "summary" ? " active" : ""
              }`}
              onClick={() => setActiveTab("summary")}
            >
              Summary
            </button>
            <button
              className={`b-ps-top-tab${
                activeTab === "explorer" ? " active" : ""
              }`}
              onClick={() => setActiveTab("explorer")}
            >
              Explorer
            </button>
          </div>

          {activeTab === "summary" && (
            <PortfolioSummary
              year={year}
              quarter={quarter}
              data={data}
              contextScoped={contextScoped}
            />
          )}

          {activeTab === "explorer" && (
            <>
              <div className="b-snapshot-summary-controls">
                <GroupingTabs active={grouping} onChange={setGrouping} />
              </div>

              {grouping === "by_portfolio_company" && (
                <div className="b-snapshot-summary-filters">
                  <div className="b-snapshot-summary-search-filter">
                    <label>Company</label>
                    <input
                      type="text"
                      className="b-snapshot-summary-search-input"
                      placeholder="Filter by company…"
                      value={filterPortcoText}
                      onChange={(e) => setFilterPortcoText(e.target.value)}
                    />
                  </div>
                  {portcoSectorOptions.length > 0 && (
                    <div className="b-snapshot-summary-pill-group">
                      <label>Sector</label>
                      {portcoSectorOptions.map((opt) => (
                        <button
                          key={opt}
                          onClick={() =>
                            toggleFilter(
                              filterPortcoSectors,
                              opt,
                              setFilterPortcoSectors,
                            )
                          }
                          className={`b-snapshot-summary-pill${
                            filterPortcoSectors.includes(opt) ? " active" : ""
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {grouping === "series" && (
                <div className="b-snapshot-summary-filters">
                  <div className="b-snapshot-summary-search-filter">
                    <label>Series</label>
                    <input
                      type="text"
                      className="b-snapshot-summary-search-input"
                      placeholder="Filter by series…"
                      value={filterSeriesText}
                      onChange={(e) => setFilterSeriesText(e.target.value)}
                    />
                  </div>
                  {vintageOptions.length > 0 && (
                    <div className="b-snapshot-summary-vintage-filter">
                      <label>Vintage</label>
                      <select
                        value={vintageFrom ?? ""}
                        onChange={(e) =>
                          setVintageFrom(
                            e.target.value
                              ? parseInt(e.target.value, 10)
                              : null,
                          )
                        }
                      >
                        <option value="">From</option>
                        {vintageOptions.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <span>–</span>
                      <select
                        value={vintageTo ?? ""}
                        onChange={(e) =>
                          setVintageTo(
                            e.target.value
                              ? parseInt(e.target.value, 10)
                              : null,
                          )
                        }
                      >
                        <option value="">To</option>
                        {vintageOptions.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {assetClassOptions.length > 0 && (
                    <div className="b-snapshot-summary-pill-group">
                      <label>Class</label>
                      {assetClassOptions.map((opt) => (
                        <button
                          key={opt}
                          onClick={() =>
                            toggleFilter(
                              filterAssetClasses,
                              opt,
                              setFilterAssetClasses,
                            )
                          }
                          className={`b-snapshot-summary-pill${
                            filterAssetClasses.includes(opt) ? " active" : ""
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {stageOptions.length > 0 && (
                    <div className="b-snapshot-summary-pill-group">
                      <label>Stage</label>
                      {stageOptions.map((opt) => (
                        <button
                          key={opt}
                          onClick={() =>
                            toggleFilter(filterStages, opt, setFilterStages)
                          }
                          className={`b-snapshot-summary-pill${
                            filterStages.includes(opt) ? " active" : ""
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {sectorOptions.length > 0 && (
                    <div className="b-snapshot-summary-pill-group">
                      <label>Sector</label>
                      {sectorOptions.map((opt) => (
                        <button
                          key={opt}
                          onClick={() =>
                            toggleFilter(filterSectors, opt, setFilterSectors)
                          }
                          className={`b-snapshot-summary-pill${
                            filterSectors.includes(opt) ? " active" : ""
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {grouping === "by_portfolio_company" ? (
                <PortfolioCompanyTable
                  key="by_portfolio_company"
                  data={data}
                  filterSectors={filterPortcoSectors}
                  filterText={filterPortcoText}
                />
              ) : (
                <PerformanceTable
                  key={grouping}
                  data={data}
                  grouping={grouping}
                  view="performance"
                  perspective={perspective}
                  vintageFrom={vintageFrom}
                  vintageTo={vintageTo}
                  filterAssetClasses={filterAssetClasses}
                  filterStages={filterStages}
                  filterSectors={filterSectors}
                  filterText={grouping === "series" ? filterSeriesText : ""}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default SnapshotSummary
